import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { signIn, resetPassword } from "../../services/auth";
import { Button, Input, Spinner } from "../../components/ui/UI";
import styles from "./Login.module.css";

export default function LoginPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user) navigate("/my-courses");
  }, [user, navigate]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);

  // Forgot password modal state
  const [forgotOpen, setForgotOpen]   = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent]   = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleLogin = async () => {
    setError("");

    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError("Enter a valid email address");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      await signIn({ email, password });
      navigate("/my-courses");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError("");
    if (!forgotEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError("Enter a valid email");
      return;
    }

    setForgotLoading(true);
    try {
      await resetPassword(forgotEmail);
      setForgotSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.icon}>🔐</div>
        <h1 className={styles.title}>Welcome back</h1>
        <p className={styles.sub}>
          Login to access your purchased courses
        </p>

        <div className={styles.form}>
          <Input
            label="Email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(""); }}
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            autoFocus
          />

          <Input
            label="Password"
            type="password"
            placeholder="Your password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(""); }}
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
          />

          {error && <p className={styles.error}>⚠ {error}</p>}

          <button
            type="button"
            className={styles.forgotLink}
            onClick={() => setForgotOpen(true)}
          >
            Forgot password?
          </button>

          <Button variant="primary" size="lg" fullWidth onClick={handleLogin} disabled={loading}>
            {loading ? <><Spinner /> Logging in…</> : "Login →"}
          </Button>

          <p className={styles.hint}>
            💡 New here? Your account is created automatically when you buy a course.
            We email your login credentials at checkout.
          </p>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {forgotOpen && (
        <div className={styles.modal} onClick={() => { setForgotOpen(false); setForgotSent(false); }}>
          <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <button className={styles.modalClose} onClick={() => { setForgotOpen(false); setForgotSent(false); }}>✕</button>

            {!forgotSent ? (
              <>
                <h3 className={styles.modalTitle}>Reset Password</h3>
                <p className={styles.modalSub}>Enter your email and we'll send you a reset link.</p>

                <Input
                  label="Email"
                  type="email"
                  placeholder="you@example.com"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleForgotPassword()}
                />

                {error && <p className={styles.error}>⚠ {error}</p>}

                <Button variant="primary" size="md" fullWidth onClick={handleForgotPassword} disabled={forgotLoading} style={{ marginTop: 16 }}>
                  {forgotLoading ? <><Spinner /> Sending…</> : "Send Reset Link"}
                </Button>
              </>
            ) : (
              <>
                <div style={{ textAlign: "center", padding: "12px 0" }}>
                  <div style={{ fontSize: 48, marginBottom: 12 }}>📧</div>
                  <h3 className={styles.modalTitle}>Check your inbox</h3>
                  <p className={styles.modalSub}>
                    We've sent a password reset link to <strong>{forgotEmail}</strong>.
                    Click the link in the email to set a new password.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
